import { useEffect, useMemo, useState } from 'react'
import { Loader2, RotateCcw } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../lib/shadcn/card'
import { Button } from '../../lib/shadcn/button'
import { Badge } from '../../lib/shadcn/badge'
import { Input } from '../../lib/shadcn/input'
import { useGetDeletedItems } from '../../hooks/backend/settingsPage'
import { useOnRestored, useRestoreItem } from '../../hooks/useUndoToast'
import { HelpText } from '../../components/HelpText'

interface DeletedItem {
  id: number
  kind: string
  label: string
  detail: string | null
  deleted_by: string | null
  deleted_at: string
  restored_at: string | null
  restored_by: string | null
}

const KIND_LABELS: Record<string, string> = {
  order: 'Order',
  order_line: 'Order item',
  unpicked_lines: 'Unpicked items',
  lead: 'Lead',
  communication: 'Message',
  tote: 'Tote',
  ingredient: 'Ingredient',
  label_recipe: 'Recipe-only flavor',
}

const when = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })

// Settings > Recently deleted: anything deleted in the last 30 days, with a
// Restore button.
export default function RecentlyDeletedSettings() {
  const getDeletedItems = useGetDeletedItems()
  const restoreItem = useRestoreItem()
  const [busy, setBusy] = useState<number | null>(null)
  const [search, setSearch] = useState('')
  const [showRestored, setShowRestored] = useState(false)

  const data = getDeletedItems.data as { keepDays: number; items: DeletedItem[] } | undefined
  const keepDays = data?.keepDays ?? 30
  const all = data?.items ?? []

  useEffect(() => {
    void getDeletedItems.trigger({})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const refresh = () => getDeletedItems.trigger({}, { skipCache: true })
  // Undo from a toast while this page is open.
  useOnRestored(Object.keys(KIND_LABELS), () => void refresh())

  const items = useMemo(() => {
    const q = search.trim().toLowerCase()
    return all.filter(
      i =>
        (showRestored || !i.restored_at) &&
        (!q ||
          `${i.label} ${i.detail ?? ''} ${KIND_LABELS[i.kind] ?? i.kind}`.toLowerCase().includes(q))
    )
  }, [all, search, showRestored])
  const restoredCount = all.filter(i => i.restored_at).length

  const restore = async (id: number) => {
    setBusy(id)
    try {
      await restoreItem(id)
      await refresh()
    } finally {
      setBusy(null)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Recently deleted</CardTitle>
        <CardDescription><HelpText>
          Orders, order items, leads, messages, totes, ingredients and recipe-only flavors you
          deleted in the last {keepDays} days. Restore puts them back as they were, including
          anything that went with them (an order's items, an ingredient's place in recipes).
        </HelpText></CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search deleted items"
            className="h-9 w-full sm:w-64"
          />
          {restoredCount > 0 && (
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                checked={showRestored}
                onChange={e => setShowRestored(e.target.checked)}
              />
              Show restored ({restoredCount})
            </label>
          )}
        </div>

        {getDeletedItems.loading && all.length === 0 ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {all.length === 0 ? `Nothing deleted in the last ${keepDays} days.` : 'Nothing matches.'}
          </p>
        ) : (
          <div className="divide-y rounded-md border">
            {items.map(item => (
              <div key={item.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium break-words">{item.label}</span>
                    <Badge variant="secondary" className="font-normal">
                      {KIND_LABELS[item.kind] ?? item.kind}
                    </Badge>
                  </div>
                  {item.detail && (
                    <p className="text-xs text-muted-foreground line-clamp-2 break-words">{item.detail}</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Deleted {when(item.deleted_at)}
                    {item.deleted_by ? ` by ${item.deleted_by}` : ''}
                    {item.restored_at
                      ? ` · Restored ${when(item.restored_at)}${item.restored_by ? ` by ${item.restored_by}` : ''}`
                      : ''}
                  </p>
                </div>
                {!item.restored_at && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1.5 shrink-0"
                    disabled={busy === item.id}
                    onClick={() => void restore(item.id)}
                  >
                    {busy === item.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RotateCcw className="h-4 w-4" />
                    )}
                    Restore
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
